import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import WhatsAppButton from "@/components/WhatsAppButton";
import { SERVICES, IMG, HOME_FAQS } from "@/lib/content";
import { useConfig } from "@/components/Layout";
import BrandImage from "@/components/BrandImage";
import GoogleReviewBadge from "@/components/GoogleReviewBadge";
import { trackEvent } from "@/lib/tracking";

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Home() {
  const config = useConfig();
  return (
    <div>
      <Seo
        title="Mabuhay – Cocktail- & Barista-Catering für Ihr Event"
        description="Professionelles Cocktail- & Barista-Catering für Messen, Firmenfeiern und Hochzeiten. Alles aus einer Hand, erfahrene Barkeeper & Baristas, mobil deutschlandweit und europaweit. Kostenloses Angebot anfordern."
        jsonLd={faqJsonLd}
        path="/"
      />

      {/* HERO */}
      <section className="relative bg-[#0e0e11] pt-16 sm:pt-24 pb-16 sm:pb-20 border-b border-[#24232a] overflow-hidden">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-72 sm:w-96 h-72 sm:h-96 opacity-10 pointer-events-none slow-drift" aria-hidden="true">
          <svg viewBox="0 0 200 200" fill="none" className="w-full h-full text-[#d97724]">
            <circle cx="100" cy="100" r="90" stroke="currentColor" strokeWidth="4" strokeDasharray="8 8" />
            <path d="M40 100 L160 100 M100 40 L100 160" stroke="currentColor" strokeWidth="2" />
          </svg>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 bg-[#1a1921] border border-[#32303c] text-[#d97724] text-xs font-mono-brand uppercase tracking-[0.14em] mb-6">
              Messen · Firmenfeiern · Hochzeiten
            </span>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold uppercase leading-[0.95] tracking-tight mb-6">
              Cocktail- &amp; Barista-Catering<br />
              <span className="text-[#d97724]">für Ihr Event.</span>
            </h1>
            <p className="text-base md:text-lg text-[#a6a29d] mb-8 max-w-xl leading-relaxed">
              Premium Bar-Service aus einer Hand: erfahrene Barkeeper & Baristas, mobile Barsysteme
              und komplette Getränkelogistik – an fast jedem Ort.
            </p>
            <div>
              <div className="flex flex-wrap items-start gap-x-4 gap-y-5">
                <div className="flex flex-col items-start">
                  <Link to="/angebot" data-testid="hero-cta-button"
                    onClick={() => trackEvent("cta_click", { page: "/", position: "hero" })}
                    className="px-8 py-4 bg-[#b35c0f] text-white text-sm font-bold uppercase tracking-wider rounded shadow-lg hover:bg-[#96500c] transition-colors">
                    Kostenloses Angebot anfordern
                  </Link>
                  <div className="mt-3.5 ml-1 min-h-[28px]">
                    <GoogleReviewBadge position="hero" />
                  </div>
                </div>
                <div className="flex flex-col items-start gap-1.5 min-h-[54px]">
                  <WhatsAppButton position="hero" large data-testid="hero-whatsapp-button" />
                  <p className="text-xs text-[#8f8a85] font-mono-brand pl-1">{config?.whatsapp_subline || "Wir melden uns sofort zurück."}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#17161c] border-b border-[#24232a] py-4 overflow-hidden" aria-hidden="true">
        <div className="marquee-track text-xs uppercase tracking-[0.2em] font-mono-brand text-[#d97724] flex items-center gap-12">
          {[...Array(2)].flatMap((_, i) => [
            <span key={`a${i}`}>Alles aus einer Hand</span>, <span key={`b${i}`}>•</span>,
            <span key={`c${i}`}>Erfahrene Barkeeper & Baristas</span>, <span key={`d${i}`}>•</span>,
            <span key={`e${i}`}>Flexibel & mobil einsetzbar</span>, <span key={`f${i}`}>•</span>,
            <span key={`g${i}`}>Eigene mobile Barsysteme</span>, <span key={`h${i}`}>•</span>,
          ])}
        </div>
      </div>

      {/* LEISTUNGEN */}
      <section id="leistungen" className="py-16 sm:py-24 bg-[#0e0e11] border-b border-[#24232a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs uppercase font-mono-brand tracking-[0.14em] text-[#d97724]">Leistungen</span>
              <h2 className="text-3xl md:text-5xl font-bold uppercase mt-2">Die passende Bar für Ihr Event</h2>
            </div>
            <p className="text-sm text-[#a6a29d] max-w-sm">
              Maßgeschneiderte Bar-Setups für geschäftliche und private Events jeder Größenordnung.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const cardImg = {
                cocktail_catering: "/img/cocktail-800.webp",
                barista_catering: "/img/barista-800.webp",
                messe_catering: "/img/messe-800.webp",
                hochzeitscatering: "/img/hochzeit-800.webp",
                personal: "/img/personal.jpg",
                cocktailkurse: "/img/kurse.jpg",
              }[s.id];
              const cardSrcSet = {
                cocktail_catering: "/img/cocktail-400.webp 400w, /img/cocktail-800.webp 800w, /img/cocktail-1200.webp 1200w",
                barista_catering: "/img/barista-400.webp 400w, /img/barista-800.webp 800w, /img/barista-1200.webp 1200w",
                messe_catering: "/img/messe-400.webp 400w, /img/messe-800.webp 800w, /img/messe-1200.webp 1200w",
                hochzeitscatering: "/img/hochzeit-400.webp 400w, /img/hochzeit-800.webp 800w, /img/hochzeit-1200.webp 1200w",
                personal: "/img/personal-400.webp 400w, /img/personal-800.webp 800w, /img/personal-1200.webp 1200w",
                cocktailkurse: "/img/kurse-400.webp 400w, /img/kurse-800.webp 800w, /img/kurse-1200.webp 1200w",
              }[s.id];
              return (
              <Reveal key={s.id} delay={i * 60}>
                <article className={`h-full bg-[#17161c] border ${s.id === "cocktailkurse" ? "border-[#d97724]/40 bg-[#1c1a24]" : "border-[#24232a]"} p-6 rounded flex flex-col justify-between`}
                  data-testid={`service-card-${s.id}`}>
                  <div>
                    <figure className="relative aspect-[16/10] bg-[#24232a] mb-5 rounded overflow-hidden">
                      <BrandImage src={cardImg} srcSet={cardSrcSet} sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={s.cardKicker} loading={i > 2 ? "lazy" : "eager"} width="640" height="400"
                        className="absolute inset-0 w-full h-full object-cover" />
                      <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0e0e11]/90 to-transparent px-4 pb-3 pt-8 text-xs uppercase tracking-[0.14em] text-[#f4efe8]">
                        {s.cardKicker}
                      </figcaption>
                    </figure>
                    {s.id === "cocktailkurse" && (
                      <span className="text-[10px] uppercase font-mono-brand px-2 py-0.5 bg-[#b35c0f]/20 text-[#d97724] rounded">Teamevent & JGA</span>
                    )}
                    <h3 className="text-xl font-bold uppercase mt-2 mb-2">{s.title}</h3>
                    <p className="text-sm text-[#a6a29d] mb-4 leading-relaxed">{s.cardText}</p>
                  </div>
                  <Link to={s.path} className="text-xs font-mono-brand uppercase tracking-wider text-[#d97724] hover:underline"
                    data-testid={`service-card-link-${s.id}`}>
                    Mehr erfahren →
                  </Link>
                </article>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VORTEILE */}
      <section className="py-16 sm:py-24 bg-[#141318] border-b border-[#24232a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="mb-12">
            <span className="text-xs uppercase font-mono-brand tracking-[0.14em] text-[#d97724]">Ihre Sicherheit</span>
            <h2 className="text-3xl md:text-5xl font-bold uppercase mt-2">Warum Veranstalter Mabuhay wählen</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { t: "Alles aus einer Hand", d: "Bar-Module, Spirituosen, Eis, Gläser, Equipment und Logistik – komplett organisiert, ohne Koordinationsaufwand für Sie." },
              { t: "Erfahrene Barkeeper & Baristas", d: "Eingespielte Profis mit echter Eventroutine, hoher Serviergeschwindigkeit und zuvorkommendem Auftreten." },
              { t: "Flexibel & mobil einsetzbar", d: "Autonome Barsysteme für Messehallen, Open-Air-Flächen, Firmenzentralen oder private Locations – deutschlandweit und europaweit." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 80}>
                <div className="border-t-2 border-[#d97724] pt-6">
                  <h3 className="text-xl font-bold uppercase mb-2">{v.t}</h3>
                  <p className="text-sm text-[#a6a29d] leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABLAUF */}
      <section className="py-16 sm:py-24 bg-[#0e0e11] border-b border-[#24232a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center max-w-xl mx-auto mb-12">
            <span className="text-xs uppercase font-mono-brand tracking-[0.14em] text-[#d97724]">Einfacher Ablauf</span>
            <h2 className="text-3xl md:text-5xl font-bold uppercase mt-2">In 3 Schritten zur mobilen Bar</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "SCHritt 1".toUpperCase(), t: "Anfrage senden", d: "Eckdaten über das Formular oder WhatsApp übermitteln. Wir prüfen Datum und Kapazitäten umgehend." },
              { n: "SCHritt 2".toUpperCase(), t: "Abstimmung & Angebot", d: "Getränkeauswahl, Bardesign und Personalbedarf abstimmen – Sie erhalten ein transparentes Angebot." },
              { n: "SCHritt 3".toUpperCase(), t: "Veranstaltung genießen", d: "Pünktlicher Aufbau, reibungsloser Barbetrieb und vollständiger Abbau – Sie feiern entspannt." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="bg-[#17161c] p-8 border border-[#24232a] rounded h-full">
                  <span className="text-xs font-mono-brand text-[#d97724] tracking-widest block mb-4">{s.n}</span>
                  <h3 className="text-xl font-bold uppercase mb-2">{s.t}</h3>
                  <p className="text-sm text-[#a6a29d] leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REFERENZEN / BEWERTUNGEN (Platzhalter, solange Google-Profil nicht verifiziert) */}
      <section className="py-16 sm:py-20 bg-[#141318] border-b border-[#24232a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <figure className="relative aspect-[16/10] rounded overflow-hidden border border-[#24232a]">
                <BrandImage src="/img/abend-800.webp" srcSet="/img/abend-400.webp 400w, /img/abend-800.webp 800w, /img/abend-1200.webp 1200w" sizes="(max-width: 768px) 100vw, 50vw" alt="Mabuhay Barcatering bei einer Abendveranstaltung" loading="lazy" width="960" height="600"
                  className="absolute inset-0 w-full h-full object-cover" />
              </figure>
              <figure className="relative aspect-[16/10] rounded overflow-hidden border border-[#24232a]">
                <BrandImage src="/img/kaffeebar-800.webp" srcSet="/img/kaffeebar-400.webp 400w, /img/kaffeebar-800.webp 800w, /img/kaffeebar-1200.webp 1200w" sizes="(max-width: 768px) 100vw, 50vw" alt="Mobile Kaffeebar von Mabuhay auf einer Messe" loading="lazy" width="960" height="600"
                  className="absolute inset-0 w-full h-full object-cover" />
              </figure>
            </div>
          </Reveal>
          <Reveal className="mt-6">
            <div className="border border-[#32303c] rounded-lg p-8 md:p-12 text-center max-w-2xl mx-auto bg-[#0e0e11]" data-testid="reviews-section">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1c1a24] text-[#e6a23c] mb-4">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold uppercase mb-2 font-display">Google-Bewertungen</h3>
              <div className="flex justify-center mb-4">
                <GoogleReviewBadge position="content" variant="card" />
              </div>
              <p className="text-sm text-[#a6a29d] leading-relaxed">
                Echte Kundenstimmen zu unseren Cocktailworkshops, Messen und Events –
                direkt im offiziellen Google-Profil nachlesbar.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24 bg-[#0e0e11] border-b border-[#24232a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal className="mb-12">
            <span className="text-xs uppercase font-mono-brand tracking-[0.14em] text-[#d97724]">Fragen & Antworten</span>
            <h2 className="text-3xl md:text-5xl font-bold uppercase mt-2">Häufige Fragen zum Catering</h2>
          </Reveal>
          <div className="space-y-4">
            {HOME_FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 40}>
                <details className="bg-[#17161c] border border-[#24232a] rounded group">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-6 text-base font-bold uppercase">
                    {f.q}
                    <span className="text-[#d97724] transition-transform group-open:rotate-45 text-xl leading-none" aria-hidden="true">+</span>
                  </summary>
                  <p className="px-6 pb-6 text-sm text-[#a6a29d] leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ANFRAGE */}
      <section id="anfrage" className="py-16 sm:py-24 bg-[#141318]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="bg-[#1c1a24] border border-[#32303c] rounded-xl p-6 sm:p-10 md:p-12 shadow-2xl">
              <div className="text-center mb-10">
                <span className="text-xs font-mono-brand uppercase text-[#d97724] tracking-widest">Unverbindlich & schnell</span>
                <h2 className="text-3xl md:text-4xl font-bold uppercase mt-2">Kostenloses Angebot anfordern</h2>
                <p className="text-sm text-[#a6a29d] mt-2">Wir melden uns mit einer transparenten Kalkulation für Ihr Event.</p>
              </div>
              <LeadForm position="content" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
